/// <amd-module name="Repos/Discussion/Editor/Integrations/ChangesTreeDiscussionIntegration" />
import * as React from "react";
import { IChangeWithDiscussionThread } from "Repos/Changes/Contracts";
import { IChange } from "Repos/ChangesCommon/Contracts";
import {
    IChangesTreeDiscussionIntegration,
    IDiffDiscussionWidget,
    IDiscussionCommentModel,
    IDiscussionThread,
    IDiscussionThreadModel,
    ILineInfo
} from "Repos/Discussion/Common/DiscussionContracts";
import * as Resources from "Repos/Discussion/Editor/Resources";
import { IObservableArrayEventArgs, IReadonlyObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { Observer } from "VSSUI/Observer";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Tooltip } from "VSSUI/TooltipEx";
import { ExpandableTreeCell, ITreeColumn } from "VSSUI/TreeEx";
import { ITreeItemEx, ITreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { ChangesTreeDiscussionWidget } from "../Widgets/ChangesTreeDiscussionWidget";
import { BaseDiscussionIntegration, IDiscussionIntegrationProps } from "./BaseDiscussionIntegration";

export interface IChangesTreeDiscussionIntegrationProps extends IDiscussionIntegrationProps {
    fileItemsProvider: ITreeItemProvider<IChange>;
}

export class ChangesTreeDiscussionIntegration extends BaseDiscussionIntegration<ChangesTreeDiscussionWidget, IChangesTreeDiscussionIntegrationProps>
    implements IChangesTreeDiscussionIntegration<ITreeItemProvider<IChange>> {
    private widget: ChangesTreeDiscussionWidget;
    private currentUser: IdentityRef;
    private filteredThreads: IReadonlyObservableArray<IDiscussionThreadModel<IDiscussionThread>>;

    constructor(props: IChangesTreeDiscussionIntegrationProps) {
        super(props);

        this.currentUser = props.currentUser;
        this.widget = new ChangesTreeDiscussionWidget({
            fileItemsProvider: props.fileItemsProvider,
            getLineNumber: props.getLineNumber,
            extractFilePath: props.extractFilePath,
            isOriginalSide: props.isOriginalSide,
            renderCell: this.onRenderThreadCell
        });

        this.initialize();

        // We observe filtered threads for filter changes where the threads become
        // visible/hidden under a file node in changes tree according to the selected filter
        this.filteredThreads = this.props.discussionStore.getFilteredThreads();
        this.filteredThreads.subscribe(this.onFilterThreads);
    }

    public dispose(): void {
        this.filteredThreads.unsubscribe(this.onFilterThreads);
        // @ts-ignore TypeScript 4.0 upgrade
        delete this.filteredThreads;

        this.widget.dispose();
        // @ts-ignore TypeScript 4.0 upgrade
        delete this.widget;

        super.dispose();
    }

    public renderAddCommentWidget(filePath: string, lineInfo: ILineInfo, addToOriginalDiff: boolean): void {
        const { createThreadModel, discussionStore, initializeThread } = this.props;
        const thread = createThreadModel();

        if (initializeThread) {
            initializeThread(thread, filePath, { addToOriginalDiff, lineInfo });
        }

        // Add thread to the discussion thread store so that UI gets updated - onUpdateThreads will be called
        discussionStore.addThread(thread);
    }

    protected addThreadToWidget(thread: IDiscussionThreadModel): void {
        if (thread.getVisible().value) {
            // Add the thread to the widget if the thread is visible
            this.widget.addThread(thread);
        }
    }

    protected createWidget(): ChangesTreeDiscussionWidget {
        // There is a single widget for whole changes tree which performs operations
        // like adding/removing thread nodes
        return this.widget;
    }

    protected filterThreads(threads: IDiscussionThreadModel[]): IDiscussionThreadModel[] {
        // We are only interested in discussion threads added to the files
        return threads.filter(t => !!this.props.extractFilePath(t));
    }

    protected getWidgetIndexByThread(threadId: number): number {
        return this.widget.containsThread(threadId) ? 0 : -1;
    }

    protected isMyWidgetInstance(widget: IDiffDiscussionWidget): boolean {
        // Since there is only one widget for whole tree, this always returns true
        return true;
    }

    protected removeThreadFromWidgets(threadId: number): void {
        // Just remove the thread from the widget
        this.widget.removeThread(threadId);
    }

    private onFilterThreads = (args: IObservableArrayEventArgs<IDiscussionThreadModel>): void => {
        const threads = (args.addedItems || []).filter(t => !!this.props.extractFilePath(t));
        for (const thread of threads) {
            const visible = thread.getVisible().value;
            const threadExists = this.widget.containsThread(thread.id);
            if (visible && !threadExists) {
                // If thread is visible and was invisible before, show it
                this.widget.addThread(thread);
            } else if (!visible && threadExists) {
                // If thread is invisible and was visible before hide it
                this.widget.removeThread(thread.id);
            }
        }
    };

    /**
     * This renders the thread node inside the changes tree right under the file it belongs to.
     */
    private onRenderThreadCell = (
        rowIndex: number,
        columnIndex: number,
        treeColumn: ITreeColumn<IChangeWithDiscussionThread>,
        treeItem: ITreeItemEx<IChangeWithDiscussionThread>
    ): JSX.Element => {
        const thread = treeItem.underlyingItem.data.thread!;
        return (
            <ExpandableTreeCell
                columnIndex={columnIndex}
                contentClassName="repos-file-explorer-tree-thread-item rhythm-horizontal-4"
                key={columnIndex}
                treeColumn={treeColumn}
                treeItem={treeItem}
            >
                <Observer firstComment={thread.getFirstComment()} comments={thread.getComments()}>
                    {(outerProps: { firstComment: IDiscussionCommentModel; comments: IDiscussionCommentModel[] }) => {
                        return (
                            <Observer
                                content={outerProps.firstComment.getContent()}
                                isDeleted={outerProps.firstComment.isDeleted()}
                                isDirty={thread.isDirty()}
                                isUnread={thread.isUnread()}
                            >
                                {(innerProps: { content: string; isDeleted: boolean; isDirty: boolean; isUnread: boolean }) => {
                                    const replyCount = outerProps.comments.filter(c => c.getId() > 0).length - 1; // -1 is for the first comment
                                    const commentAuthor = outerProps.firstComment.getAuthor() || this.currentUser;
                                    return (
                                        <>
                                            <Persona identity={commentAuthor} size={PersonaSize.size16} />
                                            <Tooltip
                                                text={format(Resources.DiscussionNodeTooltipText, commentAuthor.displayName, innerProps.content)}
                                                overflowOnly={false}
                                            >
                                                <div className="flex-grow text-ellipsis">
                                                    {(innerProps.isDirty || (!innerProps.isDeleted && !innerProps.content)) && (
                                                        <span className="margin-right-4">*</span>
                                                    )}
                                                    {innerProps.isDeleted && `[${Resources.CommentDeletedMessage}]`}
                                                    {!innerProps.isDeleted && (innerProps.content || "")}
                                                </div>
                                            </Tooltip>
                                            {innerProps.isUnread && <div className="repos-unread-comment-marker"></div>}
                                            {replyCount > 0 && (
                                                <Tooltip
                                                    text={
                                                        replyCount > 1
                                                            ? format(Resources.ReplyCountPluralMessage, replyCount)
                                                            : Resources.ReplyCountSingleMessage
                                                    }
                                                >
                                                    <span className="secondary-text body-s padding-left-4">{replyCount}</span>
                                                </Tooltip>
                                            )}
                                        </>
                                    );
                                }}
                            </Observer>
                        );
                    }}
                </Observer>
            </ExpandableTreeCell>
        );
    };
}
